import { NgIf } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormHelper } from '@common/form-helper';
import { QuestionnaireAnswers } from '@enums/questionnaire-answers';
import { DisqualifiedFieldEvent } from '@models/disqualified-field-event';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-yes-no-details-field',
  templateUrl: './yes-no-details-field.component.html',
  styleUrls: ['./yes-no-details-field.component.scss'],
  imports: [FormsModule, ReactiveFormsModule, NgIf],
})
export class YesNoDetailsFieldComponent extends FormHelper implements OnInit, AfterViewInit, OnDestroy {
  @Input() public detailsControlName: string;
  @Input() public form: FormGroup;
  @Input() public yesNoControlName: string;
  @Input() public yesNoLabel: string;
  @Input() public index: number;
  @Input() public placeholder: string = 'Type here';
  @Input() public hintText: string;
  @Input() public warningInducingOption: QuestionnaireAnswers;
  @Input() public warningMessage: string;
  @Output() public disqualifiedEvent: EventEmitter<DisqualifiedFieldEvent> = new EventEmitter<DisqualifiedFieldEvent>();

  public readonly questionnaireAnswers = QuestionnaireAnswers;
  private valueChangesSubscription: Subscription;

  /**
   * Checks if the warning-inducing option is selected.
   *
   * @returns {boolean} true if the warning-inducing option is selected; otherwise, false
   */
  public get isWarningInducingOptionSelected(): boolean {
    return this.control?.value === this.warningInducingOption;
  }

  /**
   * Gets the form control.
   *
   * @returns {AbstractControl} the form control with the specified name from the form group
   */
  private get control(): AbstractControl {
    return this.form?.get(this.yesNoControlName);
  }

  /**
   * Executes any necessary initialization logic after the component has been initialized.
   */
  public ngOnInit() {
    this.valueChangesSubscription = this.control?.valueChanges.subscribe((value) =>
      this.handleYesNoAnswerChange(value),
    );
  }

  /**
   * Executes any necessary initialization logic after the view has been initialized.
   */
  public ngAfterViewInit() {
    this.form?.get(this.yesNoControlName).addValidators(Validators.required);
  }

  /**
   * Destroys the component.
   */
  public ngOnDestroy() {
    this.form.get(this.yesNoControlName).removeValidators(Validators.required);
    this.form.get(this.detailsControlName).removeValidators(Validators.required);
    this.form.get(this.yesNoControlName).reset();
    this.form.get(this.detailsControlName).reset();
    this.valueChangesSubscription?.unsubscribe();
    this.valueChangesSubscription = undefined;
  }

  /**
   * Checks if the form control is invalid.
   *
   * @param {string} controlName the name of the form control to check for validity
   */
  public isFormControlInvalid(controlName: string): boolean {
    return this.isInvalid(this.form?.get(controlName));
  }

  /**
   * Handles the change event of the yes-no answer.
   *
   * @param {string} value the value of the yes-no answer
   */
  public handleYesNoAnswerChange(value: string): void {
    this.disqualifiedEvent.emit({
      controlName: this.yesNoControlName,
      isDisqualified: this.isWarningInducingOptionSelected,
    });

    if (value === QuestionnaireAnswers.No) {
      this.form.get(this.detailsControlName).setValue(QuestionnaireAnswers.No);

      return;
    }

    this.form.get(this.detailsControlName).reset();
  }
}
